import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../types/redux";
import { GetCardTransactionHistory } from "../../actions/card";
import { CardTransaction } from "../../types/api/card";
import CardApi from "../../api/card";
import useErrorModal from "../../hooks/useErrorModal";
import i18n from "../../i18n/config";

import FlowScreen from "../../components/Layouts/FlowScreen";
import InputSkeletonGroup from "../../components/Common/Loaders/InputSkeleton";
import TransactionsList from "../../components/MyCard/RecentTransactions/TransactionsList";
import ApiErrorModal from "../../components/Common/Modals/ApiErrorModal";
import TransactionDetails from "../../components/MyCard/RecentTransactions/TransactionDetails";
import SpinningLoader from "../../components/Common/SpinningLoader";

const RecentTransactions = () => {
  const dispatch = useDispatch();
  const card = useSelector((state: RootState) => state.card);
  const [transactionIndex, setTransactionIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [transactionToGet, setTransactionToGet] = useState<CardTransaction | null>(null);
  const [fromClick, setFromClick] = useState(false);
  const [liveMessage, setLiveMessage] = useState("");
  const liveRegionRef = useRef<HTMLDivElement | null>(null);
  const openErrorModal = useErrorModal();
  const numToGet = 20;

  useEffect(() => {
    document.querySelector("#full-height")?.classList.add("restrict-height");

    return () => {
      document.querySelector("#full-height")?.classList.remove("restrict-height");
    }
  }, []);

  useEffect(() => {
    // Check if card data is present in redux
    // i.e. if user refreshes page, we need to fetch card data again and so card.id may not be present to fetch transactions
    if (!card.id) {
      return;
    }

    // We are storing transactions in redux to try and reduce API calls
    // If card transactions are already present in redux, we don't need to fetch them again on page load
    if (card?.transactions?.length >= numToGet) {
      setTransactionIndex(card.transactions.length);
      setLoading(false);
      return;
    }

    getTransactions(transactionIndex, numToGet);
    setLoading(false);
  }, [card.id]);

  /**
   * Api call to get transactions
   * @param skip number of transactions to skip
   * @param take how many transactions to take
   */
  const getTransactions = async (skip: number, take: number) => {
    try {
      const response = await CardApi.GetCardTransactions(card.id, skip, take);
      setTransactionIndex(transactionIndex + 20);
      dispatch(GetCardTransactionHistory(response));
      setButtonLoading(false);
      if (response.length < numToGet) {
        setDisabled(true);
      }
      setLiveMessage(`${numToGet} more transactions were loaded`);
    } catch (err) {
      setLoading(false);
      setButtonLoading(false);
      openErrorModal(err);
    }
  }

  const getMoreTransactions = () => {
    setButtonLoading(true);
    getTransactions(transactionIndex, numToGet);
  }

  return (
    <FlowScreen
      flowTitle={i18n.t("PAGES.MY_CARD.TRANSACTIONS.TITLE")}
      className="v3"
      showBackButton={true}
      testId="new-recent-transactions"
    >

      {loading &&
        <InputSkeletonGroup numInputs={4} />
      }

      {!loading && card?.transactions?.length > 0 &&
        <div className="recent-transactions-container">
          <TransactionsList
            card={card}
            setTransactionToGet={setTransactionToGet}
            setFromClick={setFromClick}
            getMoreTransactions={getMoreTransactions}
            disabled={disabled}
          />

          <TransactionDetails
            transaction={transactionToGet}
            fromClick={fromClick}
            setButtonLoading={setButtonLoading}
          />

          <div
            ref={liveRegionRef}
            aria-live="polite"
            className="sr-only"
          >
            {liveMessage}
          </div>
        </div>
      }
      <SpinningLoader show={buttonLoading} />
      <ApiErrorModal />

    </FlowScreen>
  );
}

export default RecentTransactions;